import { SeadevShedBuilder } from '../ssb_builder';
import { omitKeys } from './utils';
import { findSizeRecord } from './size-record';
import { collectFormData } from './form-collector';
import { buildSnapshotPayload } from './payload-builder';
import { postSnapshotLink } from './snapshot-api';
import { GetListColorDataFn } from './types';

// Re-export all sub-modules
export * from './types';
export * from './constants';
export * from './utils';
export * from './size-record';
export * from './catalog-filters';
export * from './form-collector';
export * from './payload-builder';
export * from './snapshot-api';

/**
 * Facade class orchestrating snapshot collection, payload building, and network submission.
 * Preserves 100% backward compatibility with the original SeadevShedSnapshot API.
 */
export class SeadevShedSnapshot {
	private shedBuilder: SeadevShedBuilder;
	private getListColorData: GetListColorDataFn;

	/**
	 * @param shedBuilder Builder instance used to read canvas state and settings.
	 * @param getListColorData Callback that returns the current color list data.
	 */
	constructor(shedBuilder: SeadevShedBuilder, getListColorData: GetListColorDataFn) {
		this.shedBuilder = shedBuilder;
		this.getListColorData = getListColorData;
	}

	/**
	 * Returns a shallow copy of an object with the specified keys removed.
	 */
	public omitKeys<T extends Record<string, any>>(
		obj: T | null | undefined,
		keys: string[],
	): Partial<T> | null | undefined {
		return omitKeys(obj, keys);
	}

	/**
	 * Find a size record by width, length (depth), and height.
	 */
	public findSizeRecord = (sizes: any, width: any, length: any, height: any): any => {
		return findSizeRecord(sizes, width, length, height);
	};

	/**
	 * Builds the snapshot payload and posts it to the snapshot endpoint to create a shareable link.
	 *
	 * @param timeoutMs Request timeout in milliseconds (default: 10000).
	 * @returns The created snapshot's id, or an empty string if creation failed.
	 */
	public createSnapshotLink = async (timeoutMs = 10000): Promise<string> => {
		const raw = collectFormData(this.shedBuilder);
		const payload = buildSnapshotPayload({
			rawFormData: raw,
			shedBuilder: this.shedBuilder,
			getListColorData: this.getListColorData,
		});

		const url = this.shedBuilder.getSettingData('snapshot', 'ssb');
		return postSnapshotLink(url, payload, timeoutMs);
	};
}
